import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { FilePreview } from '@/components/files/file-preview';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/hooks/use-toast';
import { useUploadFiles, useDeleteFile } from '@/hooks/use-files';
import { getToken } from '@/lib/api';
import { formatDistanceToNow } from '@/lib/utils';
import {
  Loader2,
  Upload,
  FileText,
  Eye,
  Download,
  Trash2,
  File,
} from 'lucide-react';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

interface ProjectFilesTabProps {
  projectId: string;
  isClient: boolean;
  files: any[];
  filesLoading: boolean;
  page: number;
  onPageChange: (page: number) => void;
  pagination: { totalPages: number; total: number } | null;
}

export function ProjectFilesTab({
  projectId,
  isClient,
  files,
  filesLoading,
  page,
  onPageChange,
  pagination,
}: ProjectFilesTabProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState<any>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<any>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);

  const uploadFiles = useUploadFiles();
  const deleteFile = useDeleteFile();

  const handleFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    const overLimit = Array.from(fileList).some(
      (f) => f.size > MAX_FILE_SIZE
    );
    if (overLimit) {
      toast({
        title: 'File too large',
        description:
          'Maximum file size is 50 MB. Please select smaller files.',
        variant: 'destructive',
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('projectId', projectId);
    Array.from(fileList).forEach((file) => {
      formData.append('files', file);
    });

    try {
      await uploadFiles.mutateAsync({ formData });
      toast({
        title: 'Files uploaded',
        description: `${fileList.length} file${fileList.length !== 1 ? 's' : ''} uploaded successfully.`,
        variant: 'success',
      });
    } catch (err: any) {
      toast({
        title: 'Upload failed',
        description:
          err?.message || 'An unexpected error occurred while uploading.',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;
    setDeletingFileId(fileToDelete.id);
    setDeleteConfirmOpen(false);

    try {
      await deleteFile.mutateAsync(fileToDelete.id);
      toast({
        title: 'File deleted',
        description: `${fileToDelete.originalName} has been deleted.`,
        variant: 'success',
      });
    } catch (err: any) {
      toast({
        title: 'Failed to delete file',
        description: err?.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    } finally {
      setDeletingFileId(null);
      setFileToDelete(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-[var(--text-secondary)]">
          {pagination?.total || 0} file
          {pagination?.total !== 1 ? 's' : ''}
        </h3>
        {!isClient && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              id="project-file-upload-input"
            />
            <Button
              size="sm"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              className="gap-1.5"
            >
              {uploading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Upload size={14} />
              )}
              {uploading ? 'Uploading...' : 'Upload Files'}
            </Button>
          </>
        )}
      </div>

      {filesLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-20 rounded-lg bg-[var(--border-color)] animate-pulse"
            />
          ))}
        </div>
      ) : files.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <File size={32} className="text-[var(--text-secondary)] mb-3" />
          <p className="text-sm font-medium mb-1">
            No files uploaded yet
          </p>
          <p className="text-xs text-[var(--text-secondary)]">
            {isClient
              ? 'Files will appear here once uploaded.'
              : 'Upload files to share with the project team.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {files.map((file: any) => (
            <div
              key={file.id}
              className="flex items-center gap-3 p-3 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] hover:border-dyndev-green/20 transition-colors group"
            >
              <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-dyndev-green/10 flex items-center justify-center">
                <FileText size={16} className="text-dyndev-green" />
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {file.originalName}
                </p>
                <div className="flex items-center gap-3 text-[10px] text-[var(--text-secondary)]">
                  <span>{formatFileSize(file.size)}</span>
                  <span>{file.mimeType}</span>
                  <span>{file.uploadedBy?.name || 'Unknown'}</span>
                  <span>
                    {formatDistanceToNow(new Date(file.createdAt))}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => {
                    setPreviewFile(file);
                    setPreviewOpen(true);
                  }}
                  className="p-1.5 rounded-lg hover:bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:text-dyndev-green transition-colors"
                  title="Preview"
                >
                  <Eye size={14} />
                </button>
                <a
                  href={`/client/api/files/${file.id}/download?token=${encodeURIComponent(getToken() || '')}`}
                  className="p-1.5 rounded-lg hover:bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:text-dyndev-green transition-colors"
                  title="Download"
                >
                  <Download size={14} />
                </a>
                {!isClient && (
                  <button
                    onClick={() => {
                      setFileToDelete(file);
                      setDeleteConfirmOpen(true);
                    }}
                    disabled={deletingFileId === file.id}
                    className="p-1.5 rounded-lg hover:bg-red-500/10 text-[var(--text-secondary)] hover:text-red-400 transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-[var(--text-secondary)]">
            Page {page} of {pagination.totalPages}
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              disabled={page <= 1}
              onClick={() => onPageChange(Math.max(1, page - 1))}
            >
              Previous
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={page >= pagination.totalPages}
              onClick={() => onPageChange(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* File preview dialog */}
      <FilePreview
        file={previewFile}
        open={previewOpen}
        onOpenChange={(open) => {
          setPreviewOpen(open);
          if (!open) setPreviewFile(null);
        }}
      />

      {/* Delete file confirmation */}
      <AlertDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete file?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "
              {fileToDelete?.originalName}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
